// OrderForecastComparison.jsx
//
// Second tab in the Distributor Orders module: a SKU × month grid comparing
// the sales **Forecast** (from the sales-forecasting module's `forecasts`
// table) against the cases actually **Ordered** (distributor order line items,
// bucketed by target_ship_month), with the **Variance** (Ordered − Forecast).
//
// Adapted from the source app "Forecast Comparison" screen. That original
// was really a production-planning grid (BOM / Buffer / Prod Plan / EOM columns
// fed by the production-hub module). Brand Command has no production-hub, so
// this port keeps only the genuinely forecast-vs-orders columns — the part the
// tab is named for. The forecast read is the module's one documented
// cross-module data dependency (see ARCHITECTURE.md).
//
// Read-only. The Excel export mirrors the on-screen grid.

import React, { useState, useEffect, useMemo, useCallback, useImperativeHandle, forwardRef } from "react";
import {
  loadComparisonSupport,
  loadProductsWithDetails,
  loadForecastRowsForMonths,
  loadOrderRowsForMonths,
  hasForecastingEntitlement,
} from "../utils/ordersService";
import { Link } from "react-router-dom";
import { getSkuGroup, sortSkus } from "./skuGrouping";
import { monthNameOf, yearOf } from "../utils/monthDate.js";
import "./OrderForecastComparison.css";

const ALL_MONTHS = [
  "January","February","March","April","May","June",
  "July","August","September","October","November","December",
];

function addMonths(monthName, year, delta) {
  const idx = ALL_MONTHS.indexOf(monthName);
  const total = idx + delta;
  const newIdx = ((total % 12) + 12) % 12;
  const yearDelta = Math.floor(total / 12);
  return { month: ALL_MONTHS[newIdx], year: year + yearDelta };
}

function mkKey(sku, month, year) {
  return `${sku}|${month}|${year}`;
}

const now = new Date();
const DEFAULT_MONTH = ALL_MONTHS[now.getMonth()];
const DEFAULT_YEAR  = now.getFullYear();
const YEAR_OPTIONS  = [DEFAULT_YEAR - 1, DEFAULT_YEAR, DEFAULT_YEAR + 1];

// Per-month sub-columns. "variance" = ordered − forecast (negative = under).
const COLS = [
  { field: "forecast", label: "Forecast" },
  { field: "ordered",  label: "Ordered"  },
  { field: "variance", label: "Variance" },
];

// `onLockedChange` lets the page header know this tab has nothing to export, so
// the shared Export button can be disabled rather than writing an empty
// workbook. Kept as a callback rather than a ref read because the header has to
// re-render when the answer arrives.
const OrderForecastComparison = forwardRef(function OrderForecastComparison({ canEdit = false, onLockedChange }, ref) {
  // ── Filters ────────────────────────────────────────────────────────────────
  const [refMonth,     setRefMonth]     = useState(DEFAULT_MONTH);
  const [refYear,      setRefYear]      = useState(DEFAULT_YEAR);
  const [numMonths,    setNumMonths]    = useState(3);
  const [fRegion,      setFRegion]      = useState("");
  const [fState,       setFState]       = useState("");
  const [fDistributor, setFDistributor] = useState("");
  const [fSize,        setFSize]        = useState("");
  const [fPack,        setFPack]        = useState("");
  const [fFlavor,      setFFlavor]      = useState("");

  const [support, setSupport] = useState({
    regions: [], stateNames: [], stateRegionMap: {}, stateDistributorMap: {},
    stateDistributorsMap: {}, flavors: [], sizes: [], packs: [], distributors: [],
  });
  const [products,     setProducts]     = useState([]);
  const [forecastRows, setForecastRows] = useState([]);
  const [orderRows,    setOrderRows]     = useState([]);

  const months = useMemo(
    () => Array.from({ length: numMonths }, (_, i) => addMonths(refMonth, refYear, i)),
    [refMonth, refYear, numMonths]
  );

  // null = still checking. See hasForecastingEntitlement() for why this tab
  // needs to ask: without a plan that includes Sales Forecasting there is no
  // writer for `forecasts`, so the grid renders a Forecast column of zeros and
  // a Variance column that is just −Ordered (backlog 236/197).
  const [entitled, setEntitled] = useState(null);

  useEffect(() => {
    loadComparisonSupport().then(setSupport).catch(console.error);
    loadProductsWithDetails().then(setProducts).catch(console.error);
    hasForecastingEntitlement().then(setEntitled).catch(() => setEntitled(true));
  }, []);

  useEffect(() => { onLockedChange?.(entitled === false); }, [entitled, onLockedChange]);

  const load = useCallback(async () => {
    try {
      // Both sides now take the same {month, year} window — the order side
      // used to be handed bare month names (backlog 237).
      const [fRows, oRows] = await Promise.all([
        loadForecastRowsForMonths(months),
        loadOrderRowsForMonths(months),
      ]);
      setForecastRows(fRows);
      setOrderRows(oRows);
    } catch (err) {
      console.error("Comparison load error:", err.message);
    }
  }, [months]);

  useEffect(() => { load(); }, [load]);

  // Forecast totals per (sku, month, year), filtered by region/state/distributor.
  const forecastMap = useMemo(() => {
    const { stateRegionMap, stateDistributorMap } = support;
    const map = {};
    forecastRows.forEach(r => {
      const stateName   = r.states?.state_name || "";
      const stateRegion = stateRegionMap[stateName] || r.states?.region || "";
      const stateDist   = stateDistributorMap[stateName] || r.states?.distributor || "";
      if (fRegion      && stateRegion !== fRegion)      return;
      if (fState       && stateName   !== fState)       return;
      if (fDistributor && stateDist   !== fDistributor) return;
      const sku = r.products?.name || "";
      const k   = mkKey(sku, r.month, r.year);
      map[k] = (map[k] || 0) + (Number(r.case_forecast) || 0);
    });
    return map;
  }, [forecastRows, support, fRegion, fState, fDistributor]);

  // Ordered totals per (sku, month, YEAR), filtered by region/state/distributor.
  // ⚠️ The year in this key is the whole point of backlog 237: it used to be
  // `sku|month`, so September summed every September the tenant had ever
  // ordered for and the year picker above changed nothing. The forecast side
  // (mkKey) has always carried the year — only this side was short.
  const orderMap = useMemo(() => {
    const { stateRegionMap, stateDistributorMap } = support;
    const map = {};
    orderRows.forEach(item => {
      const order = item.order;
      if (!order) return;
      const orderRegion = stateRegionMap[order.state] || "";
      const orderDist   = stateDistributorMap[order.state] || order.distributor || "";
      if (fRegion      && orderRegion !== fRegion)      return;
      if (fState       && order.state !== fState)       return;
      if (fDistributor && orderDist   !== fDistributor) return;
      // target_ship_month is stored as "YYYY-MM-01"; key on the same
      // (name, year) vocabulary the forecast side uses.
      const k = mkKey(item.sku_name, monthNameOf(order.target_ship_month), yearOf(order.target_ship_month));
      map[k] = (map[k] || 0) + (Number(item.quantity) || 0);
    });
    return map;
  }, [orderRows, support, fRegion, fState, fDistributor]);

  function getVal(sku, month, year, field) {
    const forecast = forecastMap[mkKey(sku, month, year)] ?? 0;
    const ordered  = orderMap[mkKey(sku, month, year)] ?? 0;
    switch (field) {
      case "forecast": return forecast;
      case "ordered":  return ordered;
      case "variance": return ordered - forecast;
      default: return 0;
    }
  }

  const displaySkus = useMemo(() => {
    const filtered = products.filter(p => {
      if (fSize   && p.size   !== fSize)   return false;
      if (fPack   && p.pack   !== fPack)   return false;
      if (fFlavor && p.flavor !== fFlavor) return false;
      return true;
    });
    return sortSkus(filtered.map(p => p.name), { sizeOrder: support.sizes, packOrder: support.packs });
  }, [products, fSize, fPack, fFlavor]);

  function renderCell(sku, monthIdx, field) {
    const { month, year } = months[monthIdx];
    const val = getVal(sku, month, year, field);
    return (
      <span className={`ofc-cell-val${val < 0 ? " ofc-neg" : ""}`}>
        {val !== 0 ? val.toLocaleString() : <span className="ofc-zero">—</span>}
      </span>
    );
  }

  const stateOptions = useMemo(() => {
    if (!fRegion) return support.stateNames;
    return support.stateNames.filter(s => support.stateRegionMap[s] === fRegion);
  }, [support, fRegion]);

  const distributorOptions = useMemo(() => {
    if (!fState) return support.distributors;
    return support.stateDistributorsMap[fState] || [];
  }, [support, fState]);

  // One subtotal row per bottle size the account sells — no fixed formats.
  const sizeTotalRows = useMemo(
    () => (support.sizes || []).map((size) => ({ label: `Total ${size}`, size })),
    [support.sizes]
  );

  function getSizeTotal(size, month, year, field) {
    return displaySkus
      .filter(s => (s.split(" / ")[1] ?? "").trim() === size)
      .reduce((sum, sku) => sum + (getVal(sku, month, year, field) || 0), 0);
  }

  // ── Export ────────────────────────────────────────────────────────────────
  useImperativeHandle(ref, () => ({
    exportToExcel: async () => {
      // Loaded on demand — ExcelJS is ~400kB and only an export needs it.
      const ExcelJS = (await import("exceljs")).default;
      const wb = new ExcelJS.Workbook();
      const ws = wb.addWorksheet("Forecast vs Orders");

      const NAVY       = { argb: "FF1E3A5F" };
      const NAVY2      = { argb: "FF2D4F7C" };
      const WHITE      = { argb: "FFFFFFFF" };
      const ALT_FILL   = { argb: "FFF8FAFC" };
      const VAR_FILL   = { argb: "FFFFF3CD" };
      const TOTAL_FILL = { argb: "FFE2E8F0" };
      const totalCols  = 1 + months.length * COLS.length;

      const thick = { style: "medium", color: { argb: "FF1E3A5F" } };
      const thin  = { style: "thin",   color: { argb: "FFD1D5DB" } };

      const r1vals = ["ProductSKU"];
      months.forEach(({ month, year }) => { r1vals.push(`${month} ${year}`); for (let i = 1; i < COLS.length; i++) r1vals.push(""); });
      const r1 = ws.addRow(r1vals);
      r1.height = 20;
      r1.eachCell(cell => {
        cell.font = { bold: true, color: WHITE, size: 12 };
        cell.fill = { type: "pattern", pattern: "solid", fgColor: NAVY };
        cell.alignment = { horizontal: "center", vertical: "middle" };
      });
      months.forEach((_, mi) => {
        const sc = 2 + mi * COLS.length;
        const ec = sc + COLS.length - 1;
        ws.mergeCells(1, sc, 1, ec);
        if (mi > 0) ws.getCell(1, sc).border = { left: thick };
      });

      const r2vals = [""];
      months.forEach(() => COLS.forEach(col => r2vals.push(col.label)));
      const r2 = ws.addRow(r2vals);
      r2.height = 16;
      r2.eachCell((cell, colNum) => {
        const isVar = colNum > 1 && COLS[(colNum - 2) % COLS.length]?.field === "variance";
        cell.font  = { bold: true, color: WHITE, size: 10 };
        cell.fill  = { type: "pattern", pattern: "solid", fgColor: isVar ? { argb: "FF3A5F8A" } : NAVY2 };
        cell.alignment = { horizontal: "center", vertical: "middle" };
        cell.border = { bottom: thin };
        if (colNum > 1 && (colNum - 2) % COLS.length === 0) cell.border = { ...cell.border, left: thick };
      });

      displaySkus.forEach((sku, idx) => {
        const row = [sku];
        months.forEach(({ month, year }) => COLS.forEach(col => row.push(getVal(sku, month, year, col.field))));
        const r = ws.addRow(row);
        const isAlt = idx % 2 === 1;
        r.eachCell((cell, colNum) => {
          const isVar = colNum > 1 && COLS[(colNum - 2) % COLS.length]?.field === "variance";
          if (isVar)      cell.fill = { type: "pattern", pattern: "solid", fgColor: VAR_FILL };
          else if (isAlt) cell.fill = { type: "pattern", pattern: "solid", fgColor: ALT_FILL };
          cell.border = { bottom: thin };
          if (colNum > 1 && (colNum - 2) % COLS.length === 0) cell.border = { ...cell.border, left: thick };
          if (colNum > 1) { cell.numFmt = "#,##0"; cell.alignment = { horizontal: "right" }; }
          else { cell.alignment = { horizontal: "left" }; cell.font = { size: 10 }; }
        });
      });

      sizeTotalRows.forEach(({ label, size }) => {
        const row = [label];
        months.forEach(({ month, year }) => COLS.forEach(col => row.push(getSizeTotal(size, month, year, col.field))));
        const r = ws.addRow(row);
        r.eachCell((cell, colNum) => {
          cell.font = { bold: true, size: 10 };
          cell.fill = { type: "pattern", pattern: "solid", fgColor: TOTAL_FILL };
          cell.border = { top: thick, bottom: thin };
          if (colNum > 1 && (colNum - 2) % COLS.length === 0) cell.border = { ...cell.border, left: thick };
          if (colNum > 1) { cell.numFmt = "#,##0"; cell.alignment = { horizontal: "right" }; }
        });
      });

      ws.getColumn(1).width = 32;
      for (let i = 2; i <= totalCols; i++) ws.getColumn(i).width = 13;

      const buf = await wb.xlsx.writeBuffer();
      const blob = new Blob([buf], { type: "application/vnd.openxmlformats-officedocument.spreadsheetml.sheet" });
      const url = URL.createObjectURL(blob);
      const a = document.createElement("a");
      a.href = url;
      a.download = `ForecastVsOrders_${refMonth}${refYear}.xlsx`;
      document.body.appendChild(a);
      a.click();
      a.remove();
      setTimeout(() => URL.revokeObjectURL(url), 1000);
    },
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }), [months, displaySkus, forecastMap, orderMap, refMonth, refYear]);

  // No Sales Forecasting on this plan → say so, instead of drawing a grid of
  // zeros. Deliberately renders INSTEAD of the filter bar: every control in it
  // narrows a forecast that does not exist.
  if (entitled === false) {
    return (
      <div className="ofc-wrap">
        <div className="ofc-locked">
          <h3 className="ofc-locked-title">Forecast Comparison needs Sales Forecasting</h3>
          <p className="ofc-locked-body">
            This tab lines your forecast up against what each distributor actually
            ordered. The forecast itself is built in <strong>Sales Forecasting</strong>,
            which is part of the <strong>Scale</strong> plan — without it there is no
            forecast to compare against, so every number here would read zero.
          </p>
          <p className="ofc-locked-body">
            Your <strong>Orders</strong> and <strong>Summary</strong> tabs are unaffected.
          </p>
          <Link className="ofc-locked-cta" to="/subscribe">View plans</Link>
        </div>
      </div>
    );
  }

  return (
    <div className="ofc-wrap">
      {/* Filter bar */}
      <div className="ofc-filter-bar">
        <div className="ofc-setup-group">
          <div className="ofc-filter-group">
            <label className="ofc-filter-label">Year</label>
            <select className="ofc-filter-select ofc-setup-select" value={refYear} onChange={e => setRefYear(Number(e.target.value))}>
              {YEAR_OPTIONS.map(y => <option key={y} value={y}>{y}</option>)}
            </select>
          </div>
          <div className="ofc-filter-group">
            <label className="ofc-filter-label">Starting Month</label>
            <select className="ofc-filter-select ofc-setup-select" value={refMonth} onChange={e => setRefMonth(e.target.value)}>
              {ALL_MONTHS.map(m => <option key={m} value={m}>{m}</option>)}
            </select>
          </div>
          <div className="ofc-filter-group">
            <label className="ofc-filter-label">Number of Months</label>
            <select className="ofc-filter-select ofc-setup-select" value={numMonths} onChange={e => setNumMonths(Number(e.target.value))}>
              {Array.from({ length: 11 }, (_, i) => i + 2).map(n => <option key={n} value={n}>{n}</option>)}
            </select>
          </div>
        </div>

        <div className="ofc-filter-group">
          <label className="ofc-filter-label">Region</label>
          <select className="ofc-filter-select" value={fRegion} onChange={e => { setFRegion(e.target.value); setFState(""); }}>
            <option value="">All Regions</option>
            {support.regions.map(r => <option key={r} value={r}>{r}</option>)}
          </select>
        </div>
        <div className="ofc-filter-group">
          <label className="ofc-filter-label">State</label>
          <select className="ofc-filter-select" value={fState} onChange={e => { setFState(e.target.value); setFDistributor(""); }}>
            <option value="">All States</option>
            {stateOptions.map(s => <option key={s} value={s}>{s}</option>)}
          </select>
        </div>
        <div className="ofc-filter-group">
          <label className="ofc-filter-label">Distributor</label>
          <select className="ofc-filter-select" value={fDistributor} onChange={e => setFDistributor(e.target.value)}>
            <option value="">All Distributors</option>
            {distributorOptions.map(d => <option key={d} value={d}>{d}</option>)}
          </select>
        </div>
        <div className="ofc-filter-group">
          <label className="ofc-filter-label">Bottle Size</label>
          <select className="ofc-filter-select" value={fSize} onChange={e => setFSize(e.target.value)}>
            <option value="">All Sizes</option>
            {support.sizes.map(s => <option key={s} value={s}>{s}</option>)}
          </select>
        </div>
        <div className="ofc-filter-group">
          <label className="ofc-filter-label">Pack Size</label>
          <select className="ofc-filter-select" value={fPack} onChange={e => setFPack(e.target.value)}>
            <option value="">All Packs</option>
            {support.packs.map(p => <option key={p} value={p}>{p}</option>)}
          </select>
        </div>
        <div className="ofc-filter-group">
          <label className="ofc-filter-label">Flavor</label>
          <select className="ofc-filter-select" value={fFlavor} onChange={e => setFFlavor(e.target.value)}>
            <option value="">All Flavors</option>
            {support.flavors.map(f => <option key={f} value={f}>{f}</option>)}
          </select>
        </div>
        {(fRegion || fState || fDistributor || fSize || fPack || fFlavor) && (
          <button className="ofc-clear-btn" onClick={() => { setFRegion(""); setFState(""); setFDistributor(""); setFSize(""); setFPack(""); setFFlavor(""); }}>
            Clear Filters
          </button>
        )}
      </div>

      {/* Grid */}
      <div className="ofc-grid-scroll">
        <table className="ofc-table" style={{ tableLayout: "fixed" }}>
          <colgroup>
            <col style={{ width: 220 }} />
            {months.map(({ month, year }) =>
              COLS.map(col => <col key={`${month}${year}${col.field}`} style={{ width: 96 }} />)
            )}
          </colgroup>
          <thead>
            <tr className="ofc-month-header-row">
              <th className="ofc-sku-th" rowSpan={2}>ProductSKU</th>
              {months.map(({ month, year }) => (
                <th key={`${month}${year}`} className="ofc-month-th" colSpan={COLS.length}>
                  <div className="ofc-month-th-content"><span>{month} {year}</span></div>
                </th>
              ))}
            </tr>
            <tr className="ofc-col-header-row">
              {months.map(({ month, year }) =>
                COLS.map(col => (
                  <th key={`${month}${year}${col.field}`} className={`ofc-col-th${col.field === "variance" ? " ofc-eom-th" : ""}`}>
                    {col.label}
                  </th>
                ))
              )}
            </tr>
          </thead>
          <tbody>
            {displaySkus.map((sku, skuIdx) => {
              const prevGroup = skuIdx > 0 ? getSkuGroup(displaySkus[skuIdx - 1]) : null;
              const thisGroup = getSkuGroup(sku);
              const showGroupHeader = prevGroup !== thisGroup;
              return (
                <React.Fragment key={sku}>
                  {showGroupHeader && (
                    <tr className="ofc-group-header-row">
                      <td colSpan={1 + months.length * COLS.length} className="ofc-group-header-cell">
                        <span className="ofc-group-header-text">{thisGroup}</span>
                      </td>
                    </tr>
                  )}
                  <tr className={`ofc-data-row${skuIdx % 2 === 0 ? "" : " ofc-alt"}`}>
                    <td className="ofc-sku-cell">{sku}</td>
                    {months.map(({ month, year }, monthIdx) =>
                      COLS.map(col => (
                        <td
                          key={`${month}${year}${col.field}`}
                          className={`ofc-data-cell${col.field === "variance" ? " ofc-eom-cell" : " ofc-readonly-cell"}`}
                        >
                          {renderCell(sku, monthIdx, col.field)}
                        </td>
                      ))
                    )}
                  </tr>
                </React.Fragment>
              );
            })}
            {displaySkus.length === 0 && (
              <tr>
                <td colSpan={1 + months.length * COLS.length} className="ofc-empty">
                  No products match the current filters.
                </td>
              </tr>
            )}

            {displaySkus.length > 0 && sizeTotalRows.map(({ label, size }) => (
              <tr key={label} className="ofc-total-row">
                <td className="ofc-sku-cell ofc-total-label">{label}</td>
                {months.map(({ month, year }) =>
                  COLS.map(col => (
                    <td key={`${month}${year}${col.field}`} className={`ofc-data-cell${col.field === "variance" ? " ofc-eom-cell" : ""}`}>
                      <span className="ofc-cell-val">{getSizeTotal(size, month, year, col.field).toLocaleString() || "—"}</span>
                    </td>
                  ))
                )}
              </tr>
            ))}
            {displaySkus.length > 0 && (
              <tr className="ofc-total-row ofc-grand-total-row">
                <td className="ofc-sku-cell ofc-total-label">Total (All Cases)</td>
                {months.map(({ month, year }) =>
                  COLS.map(col => {
                    const val = sizeTotalRows.reduce((sum, r) => sum + getSizeTotal(r.size, month, year, col.field), 0);
                    return (
                      <td key={`${month}${year}${col.field}`} className={`ofc-data-cell${col.field === "variance" ? " ofc-eom-cell" : ""}`}>
                        <span className="ofc-cell-val">{val ? val.toLocaleString() : "—"}</span>
                      </td>
                    );
                  })
                )}
              </tr>
            )}
          </tbody>
        </table>
      </div>
    </div>
  );
});

export default OrderForecastComparison;
